import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Facebook from 'assets/social/facebook.svg';
import Whatsapp from 'assets/social/whatsapp.svg';
import X from 'assets/social/x.svg';
import { Copy, Share } from 'lucide-react';
import {
  handleFacebookShare,
  handleTwitterShare,
  handleWhatsAppShare,
} from 'utils/social-share';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { isMobile } from 'utils/isMobile';
import { DefaultTFuncReturn } from 'i18next';
import SendTicketToEmailSection from '../SendTicketToEmailSection/SendTicketToEmailSection';

interface ShareTicketDialogProps {
  idOrderItem: string;
  formIndex: number;
  eventId: string | null;
  buttonText?: string | DefaultTFuncReturn;
}

const ShareTicketDialog: React.FC<ShareTicketDialogProps> = ({
  idOrderItem,
  formIndex,
  eventId,
  buttonText = '',
}) => {
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const currentPageURL = `${process.env.REACT_APP_MARKETPLACE_LINK}/order/complete-attendee-form?eventId=${eventId}&orderItemId=${idOrderItem}&index=${formIndex}`;
  const { t } = useTranslation();

  const handleCopyToClipBoard = () => {
    navigator.clipboard.writeText(currentPageURL);
    setTooltipIsOpen(true);
    setTimeout(() => {
      setTooltipIsOpen(false);
    }, 1000);
  };

  const handleShare = async () => {
    setDialogIsOpen(false);

    if (navigator.share && isMobile()) {
      await navigator
        .share({
          url: currentPageURL,
          title: 'Your ticket',
        })
        .catch(e => {
          if (e instanceof DOMException && e.message.includes('canceled'))
            return;
          // if the caught error is not due to the user canceling the share, then open the dialog
          setDialogIsOpen(true);
        });
    } else {
      setDialogIsOpen(true);
    }
  };

  const handleDialogClose = () => {
    setDialogIsOpen(false);
  };

  return (
    <Dialog open={dialogIsOpen} onOpenChange={setDialogIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" onClick={handleShare}>
          <Share size={18} />
          {buttonText}
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-md"
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="title-sm">
            {t('common.shareLink')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('common.shareSR')}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center space-x-2">
          <div className="grid flex-1 gap-2">
            <Label htmlFor="link" className="sr-only">
              {t('common.link')}
            </Label>
            <Input id="link" defaultValue={currentPageURL} readOnly />
          </div>
          <TooltipProvider>
            <Tooltip open={tooltipIsOpen}>
              <TooltipTrigger>
                <Button
                  asChild
                  size="icon"
                  className="px-2!"
                  onClick={handleCopyToClipBoard}
                >
                  <Copy size={16} />
                </Button>
              </TooltipTrigger>

              <TooltipContent className="p3-medium">
                {t('common.copiedToClipboard')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div className="flex items-center justify-center gap-2">
          <hr className="w-full" />
          <span className="p2-medium text-neutral-600">{t('common.or')}</span>
          <hr className="w-full" />
        </div>

        <SendTicketToEmailSection
          idOrderItem={idOrderItem}
          formIndex={formIndex}
          eventId={eventId}
          handleDialogClose={handleDialogClose}
        />

        <div className="flex items-center justify-center gap-2">
          <hr className="w-full" />
          <span className="p2-medium text-neutral-600">{t('common.or')}</span>
          <hr className="w-full" />
        </div>
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="ghost"
            size="lg"
            onClick={() => handleFacebookShare(currentPageURL)}
          >
            <img src={Facebook} alt="Facebook" className="w-6 h-6" />
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => handleWhatsAppShare(currentPageURL, 'Your ticket')}
          >
            <img src={Whatsapp} alt="Whatsapp" className="w-6 h-6" />
          </Button>
          <Button
            variant="ghost"
            size="lg"
            onClick={() => handleTwitterShare(currentPageURL, 'Your ticket')}
          >
            <img src={X} alt="Twitter" className="w-6 h-6" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareTicketDialog;
